import { createElementBlock, createElementVNode, defineComponent, normalizeStyle, onMounted, onUnmounted, openBlock, ref, toRaw, watch } from "vue";
var BaseImageLoader = class {
	createTimeoutPromise(e, l = 3e4) {
		return Promise.race([e, new Promise((e, u) => {
			setTimeout(() => {
				u(/* @__PURE__ */ Error(`Image loading timeout after ${l}ms`));
			}, l);
		})]);
	}
	loadImageElement(e, l) {
		return new Promise((u, d) => {
			let f = new Image();
			l?.crossOrigin && (f.crossOrigin = l.crossOrigin), f.onload = () => {
				u(f);
			}, f.onerror = (l) => {
				d(/* @__PURE__ */ Error(`Failed to load image from ${e}: ${l}`));
			}, f.src = e;
		});
	}
	loadImageFromBlob(e, l) {
		return new Promise((u, d) => {
			let f = URL.createObjectURL(e);
			this.loadImageElement(f, l).then((e) => {
				URL.revokeObjectURL(f), u(e);
			}).catch((e) => {
				URL.revokeObjectURL(f), d(e);
			});
		});
	}
	loadImageFromArrayBuffer(e, l = "image/jpeg", u) {
		let d = new Blob([e], { type: l });
		return this.loadImageFromBlob(d, u);
	}
	validateImageElement(e) {
		if (!e || e.width === 0 || e.height === 0) throw Error("Invalid image: zero width or height");
	}
	validateCanvasElement(e) {
		if (!e || e.width === 0 || e.height === 0) throw Error("Invalid canvas: zero width or height");
	}
	createProgressFetch(e, l) {
		return new Promise((u, d) => {
			fetch(e).then((e) => {
				if (!e.ok) throw Error(`HTTP error! status: ${e.status}`);
				if (!e.body) {
					u(e);
					return;
				}
				let f = e.headers.get("content-length"), p = f ? parseInt(f, 10) : 0, m = 0, h = e.body.getReader(), g = [], _ = () => {
					h.read().then(({ done: d, value: f }) => {
						if (d) {
							let l = g.reduce((e, l) => e + l.length, 0), d = new Uint8Array(l), f = 0;
							for (let e of g) d.set(e, f), f += e.length;
							let p = new Blob([d]);
							u(new Response(p, {
								status: e.status,
								statusText: e.statusText,
								headers: e.headers
							}));
							return;
						}
						g.push(f), m += f.length, l && p > 0 && l({
							progress: m / p,
							loaded: m,
							total: p
						}), _();
					}).catch(d);
				};
				_();
			}).catch(d);
		});
	}
}, UrlImageLoader = class extends BaseImageLoader {
	constructor(...e) {
		super(...e), this.name = "UrlImageLoader";
	}
	canLoad(e) {
		return typeof e == "string" && (e.startsWith("http://") || e.startsWith("https://") || e.startsWith("data:image/") || e.startsWith("blob:") || e.startsWith("file:"));
	}
	async load(e, l) {
		if (!this.canLoad(e)) throw Error(`UrlImageLoader cannot load source: ${e}`);
		let u = this.loadImageElement(e, l);
		if (l?.timeout) return this.createTimeoutPromise(u, l.timeout);
		let d = await u;
		return this.validateImageElement(d), d;
	}
}, FileImageLoader = class extends BaseImageLoader {
	constructor(...e) {
		super(...e), this.name = "FileImageLoader";
	}
	canLoad(e) {
		return e instanceof File;
	}
	async load(e, l) {
		if (!this.canLoad(e)) throw Error(`FileImageLoader cannot load source: ${e}`);
		if (!this.isImageFile(e)) throw Error(`Unsupported file type: ${e.type}. Expected an image file.`);
		let u = this.loadImageFromBlob(e, l);
		if (l?.timeout) return this.createTimeoutPromise(u, l.timeout);
		let d = await u;
		return this.validateImageElement(d), d;
	}
	isImageFile(e) {
		return [
			"image/jpeg",
			"image/jpg",
			"image/png",
			"image/gif",
			"image/webp",
			"image/bmp",
			"image/svg+xml",
			"image/x-icon",
			"image/vnd.microsoft.icon"
		].some((l) => e.type === l);
	}
}, BlobImageLoader = class extends BaseImageLoader {
	constructor(...e) {
		super(...e), this.name = "BlobImageLoader";
	}
	canLoad(e) {
		return e instanceof Blob;
	}
	async load(e, l) {
		if (!this.canLoad(e)) throw Error(`BlobImageLoader cannot load source: ${e}`);
		if (!this.isImageBlob(e)) throw Error(`Unsupported blob type: ${e.type}. Expected an image blob.`);
		let u = this.loadImageFromBlob(e, l);
		if (l?.timeout) return this.createTimeoutPromise(u, l.timeout);
		let d = await u;
		return this.validateImageElement(d), d;
	}
	isImageBlob(e) {
		return e.type ? [
			"image/jpeg",
			"image/jpg",
			"image/png",
			"image/gif",
			"image/webp",
			"image/bmp",
			"image/svg+xml",
			"image/x-icon",
			"image/vnd.microsoft.icon"
		].some((l) => e.type === l) : !0;
	}
}, ArrayBufferImageLoader = class extends BaseImageLoader {
	constructor(...e) {
		super(...e), this.name = "ArrayBufferImageLoader";
	}
	canLoad(e) {
		return e instanceof ArrayBuffer || e instanceof Uint8Array || e instanceof Uint8ClampedArray || e instanceof DataView;
	}
	async load(e, l) {
		if (!this.canLoad(e)) throw Error(`ArrayBufferImageLoader cannot load source: ${e}`);
		let u;
		if (e instanceof ArrayBuffer) u = e;
		else if (e instanceof Uint8Array || e instanceof Uint8ClampedArray) u = e.buffer.slice(e.byteOffset, e.byteOffset + e.byteLength);
		else if (e instanceof DataView) u = e.buffer.slice(e.byteOffset, e.byteOffset + e.byteLength);
		else throw Error("Unsupported array buffer type");
		let d = l?.mimeType || this.detectImageFormat(u);
		if (!d) if (l?.width && l?.height) d = "image/raw-rgba";
		else throw Error("Unable to detect image format. Please specify mimeType option, or provide width/height for raw pixel data.");
		let f;
		if (d === "image/raw-rgba") {
			if (!l?.width || !l?.height) throw Error("Width and height are required for raw RGBA data");
			f = this.loadRawRGBAData(u, l.width, l.height, l);
		} else f = this.loadImageFromArrayBuffer(u, d, l);
		if (l?.timeout) return this.createTimeoutPromise(f, l.timeout);
		let p = await f;
		return this.validateImageElement(p), p;
	}
	detectImageFormat(e) {
		if (e.byteLength < 3) return null;
		let l = new Uint8Array(e);
		if (l[0] === 255 && l[1] === 216 && l[2] === 255) return "image/jpeg";
		if (e.byteLength >= 8 && l[0] === 137 && l[1] === 80 && l[2] === 78 && l[3] === 71 && l[4] === 13 && l[5] === 10 && l[6] === 26 && l[7] === 10) return "image/png";
		if (e.byteLength >= 4 && l[0] === 71 && l[1] === 73 && l[2] === 70 && l[3] === 56) return "image/gif";
		if (e.byteLength >= 12 && l[0] === 82 && l[1] === 73 && l[2] === 70 && l[3] === 70 && l[8] === 87 && l[9] === 69 && l[10] === 66 && l[11] === 80) return "image/webp";
		if (e.byteLength >= 2 && l[0] === 66 && l[1] === 77) return "image/bmp";
		let u = new TextDecoder("utf-8").decode(l.slice(0, Math.min(100, e.byteLength)));
		return u.includes("<svg") || u.includes("<?xml") ? "image/svg+xml" : null;
	}
	async loadRawRGBAData(e, l, u, d) {
		let f = l * u * 4;
		if (e.byteLength < f) throw Error(`Buffer too small for ${l}x${u} RGBA image. Expected ${f} bytes, got ${e.byteLength}`);
		let p = document.createElement("canvas");
		p.width = l, p.height = u;
		let m = p.getContext("2d");
		if (!m) throw Error("Failed to get 2D context from canvas");
		let h = m.createImageData(l, u), g = new Uint8Array(e);
		return h.data.set(g.slice(0, f)), m.putImageData(h, 0, 0), new Promise((e, l) => {
			let u = new Image();
			u.onload = () => {
				e(u);
			}, u.onerror = (e) => {
				l(/* @__PURE__ */ Error(`Failed to create image from canvas: ${e}`));
			}, u.src = p.toDataURL("image/png");
		});
	}
}, ElementImageLoader = class extends BaseImageLoader {
	constructor(...e) {
		super(...e), this.name = "ElementImageLoader";
	}
	canLoad(e) {
		return e instanceof HTMLImageElement || e instanceof HTMLCanvasElement;
	}
	async load(e, l) {
		if (!this.canLoad(e)) throw Error(`ElementImageLoader cannot load source: ${e}`);
		if (e instanceof HTMLImageElement) return e.complete || await new Promise((l, u) => {
			let d = () => {
				e.removeEventListener("load", d), e.removeEventListener("error", f), l();
			}, f = (l) => {
				e.removeEventListener("load", d), e.removeEventListener("error", f), u(/* @__PURE__ */ Error(`Image failed to load: ${l}`));
			};
			e.addEventListener("load", d), e.addEventListener("error", f);
		}), this.validateImageElement(e), e;
		if (e instanceof HTMLCanvasElement) return this.validateCanvasElement(e), e;
		throw Error("Unsupported element type");
	}
};
function isWebGPUTextureSource(e) {
	return typeof e == "object" && !!e && e.type === "texture" && e.texture && typeof e.texture == "object" && e.device && typeof e.device == "object";
}
function isWebGPUBufferSource(e) {
	return typeof e == "object" && !!e && e.type === "buffer" && e.buffer && typeof e.buffer == "object" && e.device && typeof e.device == "object" && typeof e.width == "number" && typeof e.height == "number";
}
function isWebGPUImageSource(e) {
	return isWebGPUTextureSource(e) || isWebGPUBufferSource(e);
}
var WebGPUUtils = class {
	static isSupported() {
		return "gpu" in navigator;
	}
	static async getAdapter(e) {
		if (!this.isSupported()) throw Error("WebGPU is not supported in this browser");
		return await navigator.gpu.requestAdapter(e);
	}
	static async getDevice(e, l) {
		let u = e || await this.getAdapter();
		if (!u) throw Error("Failed to get WebGPU adapter");
		return await u.requestDevice(l);
	}
	static async textureToCanvas(e, l, u) {
		if (!e || !l) throw Error("Invalid texture or device");
		let d = document.createElement("canvas");
		d.width = e.width, d.height = e.height;
		let f = d.getContext("2d");
		if (!f) throw Error("Failed to get 2D context from canvas");
		try {
			let u = e.width * e.height * 4, p = l.createBuffer({
				size: u,
				usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
				label: "ReadbackBuffer"
			});
			try {
				let u = l.createCommandEncoder();
				u.copyTextureToBuffer({ texture: e }, {
					buffer: p,
					bytesPerRow: e.width * 4
				}, [
					e.width,
					e.height,
					1
				]), l.queue.submit([u.finish()]), await l.queue.onSubmittedWorkDone(), await p.mapAsync(GPUMapMode.READ);
				let m = p.getMappedRange(), h = new ImageData(new Uint8ClampedArray(m.slice(0)), e.width, e.height);
				return p.unmap(), f.putImageData(h, 0, 0), d;
			} finally {
				p.destroy();
			}
		} catch (e) {
			throw Error(`Failed to convert texture to canvas: ${e}`);
		}
	}
	static async bufferToCanvas(e, l, u, d, f, p) {
		if (!e || !l) throw Error("Invalid buffer or device");
		let m = document.createElement("canvas");
		m.width = u, m.height = d;
		let h = m.getContext("2d");
		if (!h) throw Error("Failed to get 2D context from canvas");
		let g;
		try {
			let f = l.createBuffer({
				size: e.size,
				usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
				label: "ReadbackBuffer"
			});
			try {
				let u = l.createCommandEncoder();
				u.copyBufferToBuffer(e, 0, f, 0, e.size), l.queue.submit([u.finish()]), await l.queue.onSubmittedWorkDone(), await f.mapAsync(GPUMapMode.READ), g = f.getMappedRange().slice(0), f.unmap();
			} finally {
				f.destroy();
			}
			let p = new ImageData(new Uint8ClampedArray(g), u, d);
			return h.putImageData(p, 0, 0), m;
		} catch (e) {
			throw Error(`Failed to convert buffer to canvas: ${e}`);
		}
	}
	static getFormatBytesPerPixel(e) {
		switch (e) {
			case "rgba8unorm":
			case "rgba8unorm-srgb":
			case "bgra8unorm":
			case "bgra8unorm-srgb": return 4;
			case "rgb9e5ufloat":
			case "rgb10a2unorm":
			case "rg11b10ufloat": return 4;
			case "depth32float": return 4;
			case "depth24plus":
			case "depth24plus-stencil8": return 4;
			default: return 4;
		}
	}
}, WebGPUTextureLoader = class extends BaseImageLoader {
	constructor(...e) {
		super(...e), this.name = "WebGPUTextureLoader";
	}
	canLoad(e) {
		return isWebGPUTextureSource(e);
	}
	async load(e, l) {
		if (!this.canLoad(e)) throw Error(`WebGPUTextureLoader cannot load source: ${e}`);
		if (!WebGPUUtils.isSupported()) throw Error("WebGPU is not supported in this browser");
		let { texture: u, device: d } = e;
		if (!u || !d) throw Error("Invalid WebGPU texture source: missing texture or device");
		try {
			let e = await WebGPUUtils.textureToCanvas(u, d, l);
			return this.validateCanvasElement(e), e;
		} catch (e) {
			throw Error(`Failed to load WebGPU texture: ${e}`);
		}
	}
	async loadWithDetails(e, l) {
		if (!this.canLoad(e)) throw Error(`WebGPUTextureLoader cannot load source: ${e}`);
		if (!WebGPUUtils.isSupported()) throw Error("WebGPU is not supported in this browser");
		let { texture: u, device: d } = e;
		if (!u || !d) throw Error("Invalid WebGPU texture source: missing texture or device");
		let f = e.width || u.width, p = e.height || u.height, m = e.format || u.format || "rgba8unorm";
		try {
			let e = await WebGPUUtils.textureToCanvas(u, d, l);
			this.validateCanvasElement(e);
			let h = {
				canvas: e,
				device: d,
				metadata: {
					width: f,
					height: p,
					format: m,
					sourceType: "texture"
				}
			};
			return l?.preserveWebGPUObject && (h.webGPUObject = u), h;
		} catch (e) {
			throw Error(`Failed to load WebGPU texture: ${e}`);
		}
	}
	static createSource(e, l, u) {
		return {
			type: "texture",
			texture: e,
			device: l,
			width: u?.width || e.width,
			height: u?.height || e.height,
			format: u?.format || e.format
		};
	}
	static async createTextureFromCanvas(e, l, u) {
		if (!l) throw Error("Invalid GPU device");
		let d = u?.format || "rgba8unorm", f = u?.usage || GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.COPY_SRC, p = e.getContext("2d");
		if (!p) throw Error("Failed to get 2D context from canvas");
		let m = p.getImageData(0, 0, e.width, e.height), h;
		try {
			h = l.createTexture({
				size: [
					e.width,
					e.height,
					1
				],
				format: d,
				usage: f,
				label: "CanvasTexture"
			});
		} catch (e) {
			throw Error(`Failed to create GPU texture: ${e}`);
		}
		if (!h) throw Error("Failed to create GPU texture");
		let g;
		try {
			g = l.createBuffer({
				size: m.data.length,
				usage: GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
				label: "CanvasTextureBuffer"
			});
		} catch (e) {
			throw h.destroy(), Error(`Failed to create GPU buffer: ${e}`);
		}
		if (!g) throw h.destroy(), Error("Failed to create GPU buffer");
		try {
			l.queue.writeBuffer(g, 0, m.data);
			let u = l.createCommandEncoder();
			u.copyBufferToTexture({
				buffer: g,
				bytesPerRow: e.width * 4
			}, {
				texture: h,
				mipLevel: 0
			}, [
				e.width,
				e.height,
				1
			]), l.queue.submit([u.finish()]);
		} catch (e) {
			throw g.destroy(), h.destroy(), Error(`Failed to create texture from canvas: ${e}`);
		} finally {
			g.destroy();
		}
		return h;
	}
}, WebGPUBufferLoader = class extends BaseImageLoader {
	constructor(...e) {
		super(...e), this.name = "WebGPUBufferLoader";
	}
	canLoad(e) {
		return isWebGPUBufferSource(e);
	}
	async load(e, l) {
		if (!this.canLoad(e)) throw Error(`WebGPUBufferLoader cannot load source: ${e}`);
		if (!WebGPUUtils.isSupported()) throw Error("WebGPU is not supported in this browser");
		let { buffer: u, device: d, width: f, height: p, format: m } = e;
		if (!u || !d) throw Error("Invalid WebGPU buffer source: missing buffer or device");
		if (!f || !p) throw Error("Invalid WebGPU buffer source: missing width or height");
		try {
			let e = await WebGPUUtils.bufferToCanvas(u, d, f, p, m, l);
			return this.validateCanvasElement(e), e;
		} catch (e) {
			throw Error(`Failed to load WebGPU buffer: ${e}`);
		}
	}
	async loadWithDetails(e, l) {
		if (!this.canLoad(e)) throw Error(`WebGPUBufferLoader cannot load source: ${e}`);
		if (!WebGPUUtils.isSupported()) throw Error("WebGPU is not supported in this browser");
		let { buffer: u, device: d, width: f, height: p, format: m } = e;
		if (!u || !d) throw Error("Invalid WebGPU buffer source: missing buffer or device");
		if (!f || !p) throw Error("Invalid WebGPU buffer source: missing width or height");
		try {
			let e = await WebGPUUtils.bufferToCanvas(u, d, f, p, m, l);
			this.validateCanvasElement(e);
			let h = {
				canvas: e,
				device: d,
				metadata: {
					width: f,
					height: p,
					format: m,
					sourceType: "buffer"
				}
			};
			return l?.preserveWebGPUObject && (h.webGPUObject = u), h;
		} catch (e) {
			throw Error(`Failed to load WebGPU buffer: ${e}`);
		}
	}
	static createSource(e, l, u, d, f, p) {
		let m = WebGPUUtils.getFormatBytesPerPixel(f), h = Math.ceil(u * m / 256) * 256;
		return {
			type: "buffer",
			buffer: e,
			device: l,
			width: u,
			height: d,
			format: f,
			bytesPerRow: p?.bytesPerRow || h,
			rowsPerImage: p?.rowsPerImage || d
		};
	}
	static async createBufferFromCanvas(e, l, u = "rgba8unorm", d) {
		if (!l) throw Error("Invalid GPU device");
		let f = e.getContext("2d");
		if (!f) throw Error("Failed to get 2D context from canvas");
		let p = f.getImageData(0, 0, e.width, e.height), m = GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC | GPUBufferUsage.STORAGE, h = d?.usage || m, g;
		try {
			g = l.createBuffer({
				size: p.data.length,
				usage: h,
				label: "CanvasBuffer"
			});
		} catch (e) {
			throw Error(`Failed to create GPU buffer: ${e}`);
		}
		if (!g) throw Error("Failed to create GPU buffer");
		try {
			if ((h & GPUBufferUsage.MAP_WRITE) !== 0) {
				await g.mapAsync(GPUMapMode.WRITE);
				let e = g.getMappedRange();
				new Uint8Array(e).set(p.data), g.unmap();
			} else l.queue.writeBuffer(g, 0, p.data);
		} catch (e) {
			throw g.destroy(), Error(`Failed to write data to buffer: ${e}`);
		}
		return g;
	}
	static createBufferFromArrayBuffer(e, l, u = GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC) {
		let d = l.createBuffer({
			size: e.byteLength,
			usage: u
		});
		return l.queue.writeBuffer(d, 0, e), d;
	}
	static createBufferFromTypedArray(e, l, u = GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC) {
		let d = l.createBuffer({
			size: e.byteLength,
			usage: u
		});
		return l.queue.writeBuffer(d, 0, e.buffer), d;
	}
	static async createTextureFromBuffer(e, l, u, d, f, p) {
		if ((e.usage & GPUBufferUsage.COPY_SRC) === 0) throw Error("Buffer must have COPY_SRC usage flag for copyBufferToTexture operation");
		let m = WebGPUUtils.getFormatBytesPerPixel(f), h = Math.ceil(u * m / 256) * 256, g = p?.bytesPerRow || h, _ = p?.usage || GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.COPY_SRC, v = l.createTexture({
			size: [
				u,
				d,
				1
			],
			format: f,
			usage: _
		}), y = l.createCommandEncoder();
		return y.copyBufferToTexture({
			buffer: e,
			bytesPerRow: g
		}, {
			texture: v,
			mipLevel: 0
		}, [
			u,
			d,
			1
		]), l.queue.submit([y.finish()]), v;
	}
}, ImageLoaderFactory = class {
	static #e = this.loaders = [
		new ElementImageLoader(),
		new FileImageLoader(),
		new BlobImageLoader(),
		new ArrayBufferImageLoader(),
		new WebGPUTextureLoader(),
		new WebGPUBufferLoader(),
		new UrlImageLoader()
	];
	static getLoader(e) {
		for (let l of this.loaders) if (l.canLoad(e)) return l;
		return null;
	}
	static async loadImage(e, l) {
		let u = this.getLoader(e);
		if (!u) throw Error(`No suitable loader found for source: ${typeof e}`);
		try {
			return await u.load(e, l);
		} catch (e) {
			throw Error(`Failed to load image with ${u.name}: ${e}`);
		}
	}
	static canLoad(e) {
		return this.getLoader(e) !== null;
	}
	static getAllLoaders() {
		return [...this.loaders];
	}
	static registerLoader(e, l = this.loaders.length) {
		let u = this.loaders.findIndex((l) => l.name === e.name);
		u === -1 ? this.loaders.splice(l, 0, e) : this.loaders[u] = e;
	}
	static removeLoader(e) {
		let l = this.loaders.findIndex((l) => l.name === e);
		return l === -1 ? !1 : (this.loaders.splice(l, 1), !0);
	}
	static getSupportedTypes() {
		return [
			"string (URL, Data URL, Blob URL)",
			"HTMLImageElement",
			"HTMLCanvasElement",
			"File",
			"Blob",
			"ArrayBuffer",
			"Uint8Array",
			"Uint8ClampedArray",
			"DataView",
			"WebGPUTextureSource",
			"WebGPUBufferSource"
		];
	}
	static isWebGPUSupported() {
		return "gpu" in navigator;
	}
	static isWebGPUSource(e) {
		return isWebGPUImageSource(e);
	}
}, SplitViewerCore = class {
	constructor(e, l) {
		this.leftImg = null, this.rightImg = null, this.animationFrameId = null, this.listeners = /* @__PURE__ */ new Map(), this.boundHandlers = {}, this.isDragging = !1, this.mouseX = 0, this.mouseY = 0, this.isMouseOver = !1, this.scale = 1, this.offsetX = 0, this.offsetY = 0, this.isPinching = !1, this.lastPinchDistance = 0, this.sceneNeedsRedraw = !0, this.resizeObserver = null, this.pendingRender = !1, this.canvas = e;
		let u = e.getContext("2d");
		if (!u) throw Error("Could not get 2D context");
		this.ctx = u, this.offscreenCanvas = document.createElement("canvas");
		let d = this.offscreenCanvas.getContext("2d");
		if (!d) throw Error("Could not get offscreen 2D context");
		this.offscreenCtx = d, this.config = this.normalizeConfig(l), this.currentSplitPosition = this.config.splitPosition, this.canvas.width = this.config.width, this.canvas.height = this.config.height, this.offscreenCanvas.width = this.config.width, this.offscreenCanvas.height = this.config.height, this.initEvents(), this.loadImages();
	}
	normalizeConfig(e) {
		return {
			leftImage: e.leftImage,
			rightImage: e.rightImage,
			width: e.width,
			height: e.height,
			splitPosition: e.splitPosition ?? .5,
			splitDirection: e.splitDirection ?? "vertical",
			magnifier: {
				enabled: !1,
				size: 150,
				zoomLevel: 2,
				followCursor: !0,
				...e.magnifier
			}
		};
	}
	update(e) {
		let l = this.config.leftImage, u = this.config.rightImage, d = this.config.splitPosition;
		this.config = this.normalizeConfig({
			...this.config,
			...e
		}), d !== this.config.splitPosition && (this.currentSplitPosition = this.config.splitPosition, this.sceneNeedsRedraw = !0), (this.canvas.width !== this.config.width || this.canvas.height !== this.config.height) && (this.canvas.width = this.config.width, this.canvas.height = this.config.height), l !== this.config.leftImage || u !== this.config.rightImage ? this.loadImages() : this.sceneNeedsRedraw && this.requestDebouncedRender();
	}
	async loadImages() {
		try {
			let e = async (e) => {
				if (e && typeof e == "object" && "source" in e && "width" in e && "height" in e) {
					let { source: l, width: u, height: d, mimeType: f } = e;
					return ImageLoaderFactory.loadImage(l, {
						width: u,
						height: d,
						mimeType: f
					});
				}
				return ImageLoaderFactory.loadImage(e);
			}, [l, u] = await Promise.all([e(this.config.leftImage), e(this.config.rightImage)]);
			this.leftImg = l, this.rightImg = u, this.canvas.width = this.config.width, this.canvas.height = this.config.height, this.sceneNeedsRedraw = !0, this.requestDebouncedRender(), this.emit("image-load", "all");
		} catch (e) {
			console.error("Failed to load images", e), this.emit("image-load", "error");
		}
	}
	initEvents() {
		let e = (e) => {
			let l = this.canvas.getBoundingClientRect(), u = "touches" in e && e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX, d = "touches" in e && e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY;
			return {
				x: u - l.left,
				y: d - l.top
			};
		}, l = (e) => {
			let l = this.canvas.getBoundingClientRect(), u = "touches" in e && e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX, d = "touches" in e && e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY;
			return {
				x: u - l.left,
				y: d - l.top
			};
		}, u = (e) => Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY), d = (e) => {
			let l = this.canvas.getBoundingClientRect();
			return {
				x: (e.touches[0].clientX + e.touches[1].clientX) / 2 - l.left,
				y: (e.touches[0].clientY + e.touches[1].clientY) / 2 - l.top
			};
		}, f = (l) => {
			if (window.TouchEvent && l instanceof TouchEvent && l.touches.length === 2) {
				this.isPinching = !0, this.isDragging = !1, this.lastPinchDistance = u(l), l.preventDefault();
				return;
			}
			let { x: d, y: f } = e(l);
			this.isDragging = !0, this.handleMove(d, f);
		}, p = (l) => {
			let { x: u, y: d } = e(l);
			this.mouseX = u, this.mouseY = d, this.isMouseOver = !0, this.emit("mouse-move", u, d), this.isDragging ? this.handleMove(u, d) : this.config.magnifier.enabled && this.config.magnifier.followCursor && (this.requestDebouncedRender(), this.emit("magnifier-move", u, d));
		}, m = () => {
			this.isDragging = !1, this.isPinching = !1;
		};
		this.boundHandlers = {
			mousedown: f,
			touchstart: f,
			windowMouseMove: (e) => {
				if (this.isDragging) {
					let { x: u, y: d } = l(e);
					this.mouseX = u, this.mouseY = d, this.isMouseOver = !1, this.emit("mouse-move", u, d), this.handleMove(u, d);
				}
			},
			windowTouchMove: (e) => {
				if (this.isPinching && e.touches.length === 2) {
					e.preventDefault();
					let l = u(e), f = d(e);
					if (this.lastPinchDistance > 0) {
						let e = l / this.lastPinchDistance, u = this.scale * e;
						if (u >= .01 && u <= 1e3) {
							let e = f.x, l = f.y, d = (e - this.offsetX) / this.scale, p = (l - this.offsetY) / this.scale;
							this.offsetX = e - d * u, this.offsetY = l - p * u, this.scale = u, this.sceneNeedsRedraw = !0, this.requestDebouncedRender();
						}
					}
					this.lastPinchDistance = l;
					return;
				}
				if (this.isDragging) {
					let { x: u, y: d } = l(e);
					this.mouseX = u, this.mouseY = d, this.isMouseOver = !1, this.handleMove(u, d);
				}
			},
			windowMouseUp: m,
			windowTouchEnd: m,
			canvasMouseMove: (e) => {
				this.isDragging || p(e);
			},
			canvasTouchMove: (e) => {
				this.isDragging || p(e);
			},
			canvasMouseLeave: () => {
				this.isMouseOver = !1, this.isDragging = !1, this.requestDebouncedRender();
			},
			handleWheel: this.handleWheel.bind(this)
		}, this.canvas.addEventListener("mousedown", this.boundHandlers.mousedown), this.canvas.addEventListener("touchstart", this.boundHandlers.touchstart, { passive: !1 }), this.canvas.addEventListener("mousemove", this.boundHandlers.canvasMouseMove), this.canvas.addEventListener("touchmove", this.boundHandlers.canvasTouchMove, { passive: !1 }), this.canvas.addEventListener("mouseleave", this.boundHandlers.canvasMouseLeave), window.addEventListener("mousemove", this.boundHandlers.windowMouseMove), window.addEventListener("touchmove", this.boundHandlers.windowTouchMove, { passive: !1 }), window.addEventListener("mouseup", this.boundHandlers.windowMouseUp), window.addEventListener("touchend", this.boundHandlers.windowTouchEnd), this.canvas.addEventListener("wheel", this.boundHandlers.handleWheel, { passive: !1 }), this.initResizeObserver();
	}
	initResizeObserver() {
		typeof ResizeObserver > "u" || (this.resizeObserver = new ResizeObserver((e) => {
			for (let l of e) {
				let { width: e, height: u } = l.contentRect;
				(e !== this.config.width || u !== this.config.height) && (this.config.width = e, this.config.height = u, this.canvas.width = e, this.canvas.height = u, this.sceneNeedsRedraw = !0, this.requestDebouncedRender());
			}
		}), this.resizeObserver.observe(this.canvas));
	}
	requestDebouncedRender() {
		this.pendingRender || (this.pendingRender = !0, requestAnimationFrame(() => {
			this.render(), this.pendingRender = !1;
		}));
	}
	handleWheel(e) {
		e.preventDefault();
		let l = this.canvas.getBoundingClientRect(), u = e.clientX - l.left, d = e.clientY - l.top, f = .1, p = e.deltaY < 0 ? 1 + f : 1 / (1 + f), m = this.scale * p;
		if (m < .01 || m > 1e3) return;
		let h = (u - this.offsetX) / this.scale, g = (d - this.offsetY) / this.scale;
		this.offsetX = u - h * m, this.offsetY = d - g * m, this.scale = m, this.sceneNeedsRedraw = !0, this.requestDebouncedRender();
	}
	handleMove(e, l) {
		this.config.splitDirection === "vertical" ? this.currentSplitPosition = Math.max(0, Math.min(1, e / this.config.width)) : this.currentSplitPosition = Math.max(0, Math.min(1, l / this.config.height)), this.sceneNeedsRedraw = !0, this.requestDebouncedRender(), this.emit("split-change", this.currentSplitPosition);
	}
	render() {
		!this.leftImg || !this.rightImg || ((this.offscreenCanvas.width !== this.config.width || this.offscreenCanvas.height !== this.config.height) && (this.offscreenCanvas.width = this.config.width, this.offscreenCanvas.height = this.config.height, this.sceneNeedsRedraw = !0), this.sceneNeedsRedraw &&= (this.offscreenCtx.clearRect(0, 0, this.config.width, this.config.height), this.drawSceneToContext(this.offscreenCtx, this.scale, this.offsetX, this.offsetY), !1), this.ctx.clearRect(0, 0, this.config.width, this.config.height), this.ctx.drawImage(this.offscreenCanvas, 0, 0), this.config.magnifier.enabled && this.isMouseOver && this.drawMagnifier());
	}
	drawSceneToContext(e, l, u, d) {
		let f = this.config.width, p = this.config.height, m = this.config.splitDirection === "vertical" ? f * this.currentSplitPosition : p * this.currentSplitPosition;
		this.drawTiledImageToContext(e, this.leftImg, f, p, l, u, d), e.save(), e.beginPath(), this.config.splitDirection === "vertical" ? e.rect(m, 0, f - m, p) : e.rect(0, m, f, p - m), e.clip(), this.drawTiledImageToContext(e, this.rightImg, f, p, l, u, d), e.restore(), e.beginPath(), e.strokeStyle = "#fff", e.lineWidth = 2, this.config.splitDirection === "vertical" ? (e.moveTo(m, 0), e.lineTo(m, p)) : (e.moveTo(0, m), e.lineTo(f, m)), e.stroke();
	}
	drawTiledImageToContext(e, l, u, d, f, p, m) {
		let h = (0 - p) / f, g = (0 - m) / f, _ = (u - p) / f, v = (d - m) / f, y = l.width, b = l.height;
		if (f < .5) {
			let u = e.createPattern(l, "repeat");
			if (!u) return;
			e.save(), e.translate(p, m), e.scale(f, f), e.fillStyle = u;
			let d = h, y = g, b = _ - h, x = v - g;
			e.fillRect(d, y, b, x), e.restore();
			return;
		}
		let x = Math.floor(h / y), S = Math.floor(_ / y), C = Math.floor(g / b), w = Math.floor(v / b);
		for (let h = x; h <= S; h++) for (let g = C; g <= w; g++) {
			let _ = h * y * f + p, v = g * b * f + m, x = y * f, S = b * f;
			_ + x < 0 || _ > u || v + S < 0 || v > d || e.drawImage(l, _, v, x, S);
		}
	}
	drawMagnifier() {
		let { size: e, zoomLevel: l } = this.config.magnifier, u = e / 2, d = this.mouseX, f = this.mouseY;
		this.ctx.save(), this.ctx.beginPath(), this.ctx.arc(d, f, u, 0, Math.PI * 2), this.ctx.clip(), this.ctx.fillStyle = "#fff", this.ctx.fill();
		let p = e / l, m = d - p / 2, h = f - p / 2;
		this.ctx.drawImage(this.offscreenCanvas, m, h, p, p, d - u, f - u, e, e), this.ctx.restore(), this.ctx.beginPath(), this.ctx.arc(d, f, u, 0, Math.PI * 2), this.ctx.strokeStyle = "rgba(255, 255, 255, 0.8)", this.ctx.lineWidth = 3, this.ctx.stroke(), this.ctx.shadowColor = "rgba(0,0,0,0.3)", this.ctx.shadowBlur = 10, this.ctx.stroke();
	}
	on(e, l) {
		this.listeners.has(e) || this.listeners.set(e, /* @__PURE__ */ new Set()), this.listeners.get(e).add(l);
	}
	off(e, l) {
		this.listeners.get(e)?.delete(l);
	}
	setSplitPosition(e) {
		let l = Math.max(0, Math.min(1, e));
		this.currentSplitPosition !== l && (this.currentSplitPosition = l, this.sceneNeedsRedraw = !0, this.requestDebouncedRender(), this.emit("split-change", this.currentSplitPosition));
	}
	getSplitPosition() {
		return this.currentSplitPosition;
	}
	setMagnifierPosition(e, l) {
		this.mouseX = Math.max(0, Math.min(this.config.width, e)), this.mouseY = Math.max(0, Math.min(this.config.height, l)), this.config.magnifier.enabled && (this.isMouseOver = !0, this.requestDebouncedRender(), this.emit("magnifier-move", this.mouseX, this.mouseY));
	}
	getMagnifierPosition() {
		return {
			x: this.mouseX,
			y: this.mouseY
		};
	}
	setZoom(e, l, u) {
		let d = Math.max(.01, Math.min(1e3, e)), f = l === void 0 ? this.config.width / 2 : l, p = u === void 0 ? this.config.height / 2 : u, m = (f - this.offsetX) / this.scale, h = (p - this.offsetY) / this.scale;
		this.offsetX = f - m * d, this.offsetY = p - h * d, this.scale = d, this.sceneNeedsRedraw = !0, this.requestDebouncedRender();
	}
	getZoom() {
		return this.scale;
	}
	setOffset(e, l) {
		this.offsetX = e, this.offsetY = l, this.sceneNeedsRedraw = !0, this.requestDebouncedRender();
	}
	getOffset() {
		return {
			x: this.offsetX,
			y: this.offsetY
		};
	}
	resetZoom() {
		this.scale = 1, this.offsetX = 0, this.offsetY = 0, this.sceneNeedsRedraw = !0, this.requestDebouncedRender();
	}
	emit(e, ...l) {
		this.listeners.get(e)?.forEach((e) => e(...l));
	}
	destroy() {
		this.boundHandlers.mousedown && this.canvas.removeEventListener("mousedown", this.boundHandlers.mousedown), this.boundHandlers.touchstart && this.canvas.removeEventListener("touchstart", this.boundHandlers.touchstart), this.boundHandlers.canvasMouseMove && this.canvas.removeEventListener("mousemove", this.boundHandlers.canvasMouseMove), this.boundHandlers.canvasTouchMove && this.canvas.removeEventListener("touchmove", this.boundHandlers.canvasTouchMove), this.boundHandlers.canvasMouseLeave && this.canvas.removeEventListener("mouseleave", this.boundHandlers.canvasMouseLeave), this.boundHandlers.handleWheel && this.canvas.removeEventListener("wheel", this.boundHandlers.handleWheel), this.boundHandlers.windowMouseMove && window.removeEventListener("mousemove", this.boundHandlers.windowMouseMove), this.boundHandlers.windowTouchMove && window.removeEventListener("touchmove", this.boundHandlers.windowTouchMove), this.boundHandlers.windowMouseUp && window.removeEventListener("mouseup", this.boundHandlers.windowMouseUp), this.boundHandlers.windowTouchEnd && window.removeEventListener("touchend", this.boundHandlers.windowTouchEnd), this.resizeObserver &&= (this.resizeObserver.disconnect(), null), this.listeners.clear(), this.leftImg = null, this.rightImg = null, this.animationFrameId !== null && (cancelAnimationFrame(this.animationFrameId), this.animationFrameId = null), this.pendingRender = !1;
	}
}, SplitViewer_default = /* @__PURE__ */ ((e, l) => {
	let u = e.__vccOpts || e;
	for (let [e, d] of l) u[e] = d;
	return u;
})(/* @__PURE__ */ defineComponent({
	__name: "SplitViewer",
	props: {
		leftImage: {},
		rightImage: {},
		width: {},
		height: {},
		splitPosition: { default: .5 },
		splitDirection: { default: "vertical" },
		magnifier: { default: () => ({
			enabled: !1,
			size: 150,
			zoomLevel: 2,
			followCursor: !0
		}) }
	},
	emits: [
		"split-change",
		"magnifier-move",
		"image-load",
		"mouse-move",
		"webgpu-error"
	],
	setup(u, { expose: v, emit: y }) {
		let b = u, x = y, S = ref(null), C = null, w = () => {
			if (!S.value) return;
			let e = (e) => ImageLoaderFactory.isWebGPUSource(e) && !ImageLoaderFactory.isWebGPUSupported() ? (x("webgpu-error", "WebGPU is not supported in this browser"), !1) : !0;
			if (!e(b.leftImage) || !e(b.rightImage)) return;
			let l = {
				leftImage: toRaw(b.leftImage),
				rightImage: toRaw(b.rightImage),
				width: b.width,
				height: b.height,
				splitPosition: b.splitPosition,
				splitDirection: b.splitDirection,
				magnifier: toRaw(b.magnifier)
			};
			try {
				C = new SplitViewerCore(S.value, l), C.on("split-change", (e) => x("split-change", e)), C.on("magnifier-move", (e, l) => x("magnifier-move", e, l)), C.on("image-load", (e) => x("image-load", e)), C.on("mouse-move", (e, l) => x("mouse-move", e, l));
			} catch (e) {
				throw (ImageLoaderFactory.isWebGPUSource(b.leftImage) || ImageLoaderFactory.isWebGPUSource(b.rightImage)) && x("webgpu-error", `Failed to initialize WebGPU viewer: ${e}`), e;
			}
		};
		return watch(() => b, (e) => {
			if (C) {
				let l = (e) => ImageLoaderFactory.isWebGPUSource(e) && !ImageLoaderFactory.isWebGPUSupported() ? (x("webgpu-error", "WebGPU is not supported in this browser"), !1) : !0;
				if (!l(e.leftImage) || !l(e.rightImage)) return;
				try {
					C.update({
						leftImage: toRaw(e.leftImage),
						rightImage: toRaw(e.rightImage),
						width: e.width,
						height: e.height,
						splitPosition: e.splitPosition,
						splitDirection: e.splitDirection,
						magnifier: toRaw(e.magnifier)
					});
				} catch (l) {
					throw (ImageLoaderFactory.isWebGPUSource(e.leftImage) || ImageLoaderFactory.isWebGPUSource(e.rightImage)) && x("webgpu-error", `Failed to update WebGPU viewer: ${l}`), l;
				}
			}
		}, { deep: !0 }), onMounted(() => {
			w();
		}), onUnmounted(() => {
			C &&= (C.destroy(), null);
		}), v({
			setSplitPosition: (e) => {
				C && C.setSplitPosition(e);
			},
			getSplitPosition: () => C ? C.getSplitPosition() : 0,
			setMagnifierPosition: (e, l) => {
				C && C.setMagnifierPosition(e, l);
			},
			getMagnifierPosition: () => C ? C.getMagnifierPosition() : {
				x: 0,
				y: 0
			},
			setZoom: (e, l, u) => {
				C && C.setZoom(e, l, u);
			},
			getZoom: () => C ? C.getZoom() : 1,
			setOffset: (e, l) => {
				C && C.setOffset(e, l);
			},
			getOffset: () => C ? C.getOffset() : {
				x: 0,
				y: 0
			},
			resetZoom: () => {
				C && C.resetZoom();
			}
		}), (f, p) => (openBlock(), createElementBlock("div", {
			class: "split-viewer-container",
			style: normalizeStyle({
				width: u.width + "px",
				height: u.height + "px"
			})
		}, [createElementVNode("canvas", {
			ref_key: "canvasRef",
			ref: S
		}, null, 512)], 4));
	}
}), [["__scopeId", "data-v-25f93978"]]);
export { ArrayBufferImageLoader, BaseImageLoader, BlobImageLoader, ElementImageLoader, FileImageLoader, ImageLoaderFactory, SplitViewer_default as SplitViewer, SplitViewerCore, UrlImageLoader, WebGPUBufferLoader, WebGPUTextureLoader, WebGPUUtils, isWebGPUBufferSource, isWebGPUImageSource, isWebGPUTextureSource };
